import React, { useState, useEffect } from 'react';
import { useNavigate } from '../i18n/router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  MdAdd,
  MdDelete,
  MdFlightTakeoff,
  MdCheckCircle,
  MdHelpOutline,
} from 'react-icons/md';
import DescriptionIcon from '@mui/icons-material/Description';
import CardSkeleton from '../components/CardSkeleton';
import Header from '../components/Header';
import { Demandes } from '../models/Demandes';
import {
  recupererListeDemandes,
  supprimerDemandeService,
  payer,
  checkUserSociete,
  getDossiersNonPayes,
} from '../Services';
import { handleFlagError } from '../utils/flag';
import { PAIEMENT_ACTIF } from '../config/features';
const DemandesScreen: React.FC = () => {
  const { t } = useTranslation('screens');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  /* ------------------------------------------------------------------ */
  /* STATES                                                             */
  /* ------------------------------------------------------------------ */
  const [isLoading, setIsLoading] = useState(true);
  const [demandes, setDemandes] = useState<Demandes[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [hideFees] = useState<boolean>(false);

  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [hasUnpaidDossiers, setHasUnpaidDossiers] = useState<boolean>(false);
  const [, setTotalUnpaid] = useState<number>(0);

  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoDialogMessage, setInfoDialogMessage] = useState('');

  const handleInfoOpen = (message: string) => {
    setInfoDialogMessage(message);
    setInfoDialogOpen(true);
  };

  const handleInfoClose = () => {
    setInfoDialogOpen(false);
    setInfoDialogMessage('');
  };

  /* ------------------------------------------------------------------ */
  /* PETIT COMPOSANT << InfoIcon >>  réutilisable                       */
  /* ------------------------------------------------------------------ */
  const InfoIcon: React.FC<{ message: string }> = ({ message }) => (
    <Tooltip title={message}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleInfoOpen(message);
        }}
        sx={{ p: 0, ml: 0.5 }}
      >
        <MdHelpOutline />
      </IconButton>
    </Tooltip>
  );

  /* ------------------------------------------------------------------ */
  /* SELECTION DES CARTES                                               */
  /* ------------------------------------------------------------------ */
  const handleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  /* ------------------------------------------------------------------ */
  /* RÉCUPÉRATION DES DEMANDES                                          */
  /* ------------------------------------------------------------------ */
  const fetchDemandes = async () => {
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const demandesData = await recupererListeDemandes(user.id_utilisateur);
        setDemandes(demandesData);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes :', error);
    }
  };

  /* ------------------------------------------------------------------ */
  /* VÉRIFICATION SI UTILISATEUR APPARTIENT À UNE SOCIÉTÉ               */
  /* ------------------------------------------------------------------ */
  const checkUserStatus = async () => {
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const societeResult = await checkUserSociete(user.id_utilisateur);
        setIsClientSociete(societeResult.isSociete);
        if (!societeResult.isSociete) {
          const unpaidResult = await getDossiersNonPayes(user.id_utilisateur);
          setHasUnpaidDossiers(unpaidResult.hasUnpaid);
          setTotalUnpaid(unpaidResult.total);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du statut utilisateur:', error);
      setIsClientSociete(true);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDemandes();
    checkUserStatus();
    const handleFocus = () => {
      fetchDemandes();
      checkUserStatus();
      setSelectedIds([]);
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  /* ------------------------------------------------------------------ */
  /* ACTIONS                                                            */
  /* ------------------------------------------------------------------ */
  const accederFacture = (cheminDemande: any) => {
    window.open(
      `https://betravel-common.visamonde.com/demandes/${cheminDemande}/pax.pdf`,
      '_blank'
    );
  };

  const handlePayer = async () => {
    if (selectedIds.length === 0) {
      handleInfoOpen(t('demandes.snackbar.selectAtLeastOne'));
      return;
    }
    setIsLoading(true);
    try {
      const { urlPaiement, bonId } = await payer(selectedIds);
      navigate('/paiement', { state: { bonId, urlPaiement } });
    } catch (error) {
      console.error('Erreur lors du paiement :', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayerSumup = () => {
    if (selectedIds.length === 0) {
      handleInfoOpen(PAIEMENT_ACTIF ? t('demandes.snackbar.selectToPay') : t('demandes.snackbar.selectToLaunch'));
      return;
    }
    navigate('/paiement', {
      state: {
        bonId: 0,
        urlPaiement: '',
        demandeIds: selectedIds,
        modePaiementSumup: true,
      },
    });
  };

  const handleDeleteDemandeConfirmation = (id: number) => {
    if (window.confirm(t('demandes.deleteConfirm'))) {
      handleDeleteDemande(id);
    }
  };

  const handleDeleteDemande = async (id: number) => {
    setIsLoading(true);
    try {
      await supprimerDemandeService(id);
      setSelectedIds((prev) => prev.filter((sid) => sid !== id));
      fetchDemandes();
    } catch (error) {
      console.error('Erreur lors de la suppression :', error);
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Helper: couleur de statut                                          */
  /* ------------------------------------------------------------------ */
  const getStatusColor = (item: Demandes): string => {
    if (item.alerte === 'oui') return '#e53935';
    if (item.etat_visa === 'en cours') return '#2e7d32';
    if (item.etat_paiement === 'attente paiement' || item.etat_visa === 'Attente paiement') return '#ff9800';
    if (item.etat_paiement === 'payé' || item.etat_paiement === 'virement' || item.etat_paiement === 'à facturer') return '#2e7d32';
    if (item.etat_visa === 'Vérification éligibilité') return '#fdd835';
    if (item.infosdemande) return '#F5C770';
    return '#e0e0e0';
  };

  /* ------------------------------------------------------------------ */
  /* RENDER                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <Box
      sx={{
        backgroundImage: 'linear-gradient(180deg, #FFF8EC 0%, #FAF3E2 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        overflowY: 'auto',
      }}
    >
      <Header />

      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
        {/* Page Title (desktop) */}
        {isDesktop && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: '#1A1A1A', fontFamily: "'Fraunces', serif" }}
            >
              {t('demandes.title')}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: '#4A4A4A', mt: 0.5 }}
            >
              {t('demandes.subtitle')}
            </Typography>
          </Box>
        )}

        {/* Bouton « Créer une demande » */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'center', sm: 'flex-start' },
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<MdAdd size={20} />}
            onClick={() => navigate('/retour', { state: { tabValue: 0 } })}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              py: 1.5,
              px: { xs: 3, sm: 4 },
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
              boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #B5811F 0%, #8C6418 100%)',
                boxShadow: '0 6px 20px rgba(46,125,50,0.4)',
              },
            }}
          >
            {t('demandes.createBtn')}
          </Button>
        </Box>

        {/* Zone d'instruction */}
        {!hideFees && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: 'rgba(255,255,255,0.9)',
              borderRadius: 3,
              backdropFilter: 'blur(8px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <Typography variant="body2" align="center" sx={{ color: '#555', fontWeight: 500 }}>
              {t('demandes.instruction')}
            </Typography>
          </Box>
        )}

        {/* Liste des demandes */}
        {isLoading ? (
          <CardSkeleton count={6} variant="demande" />
        ) : demandes.length === 0 ? (
          /* Empty state */
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 3,
              backgroundColor: 'rgba(255,255,255,0.9)',
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              backdropFilter: 'blur(8px)',
              maxWidth: 500,
              mx: 'auto',
              mt: 4,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#555', mb: 1 }}>
              {t('demandes.empty.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              {t('demandes.empty.subtitle')}
            </Typography>
          </Box>
        ) : (
          <Grid
            container
            spacing={2}
            sx={{ pb: !hideFees ? '100px' : 0 }}
          >
            {demandes.map((item, index) => {
              const isSelected = selectedIds.includes(item.id!);
              const statusColor = getStatusColor(item);

              const isExceptionForNonSociete = !hideFees &&
                item.etat_visa === 'En attente réception dossier' &&
                item.etat_paiement === 'Attente facturation';

              const isExceptionForSociete = isClientSociete === true &&
                item.etat_paiement === 'Attente facturation';

              const isNonSelectable = !isExceptionForNonSociete && !isExceptionForSociete && (
                item.etat_visa === 'en cours' ||
                item.etat_visa === 'Vérification éligibilité' ||
                item.etat_visa === 'En attente réception dossier' ||
                item.etat_paiement === 'payé' ||
                item.etat_paiement === 'facturé' ||
                item.etat_paiement === 'à facturer' ||
                item.etat_paiement === 'virement' ||
                item.etat_paiement === 'attente paiement'
              );

              const isRemovable =
                !hideFees &&
                item.etat_paiement !== 'payé' &&
                item.etat_paiement !== 'facturé' &&
                item.etat_paiement !== 'à facturer' &&
                item.etat_paiement !== 'virement';

              return (
                <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                  <Card
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: isNonSelectable && !hideFees ? 'default' : 'pointer',
                      backgroundColor: isSelected ? '#e3f2fd' : '#fff',
                      boxShadow: isSelected
                        ? '0 0 0 3px #D89D2F, 0 4px 20px rgba(25,118,210,0.25)'
                        : '0 2px 12px rgba(0,0,0,0.08)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: isDesktop ? 'translateY(-4px)' : 'none',
                        boxShadow: isSelected
                          ? '0 0 0 3px #D89D2F, 0 8px 30px rgba(25,118,210,0.3)'
                          : isDesktop
                            ? '0 8px 30px rgba(0,0,0,0.15)'
                            : '0 2px 12px rgba(0,0,0,0.08)',
                      },
                    }}
                    onClick={() => {
                      if (!isNonSelectable && !hideFees) handleSelect(item.id!);
                    }}
                  >
                    {/* Top status bar */}
                    <Box sx={{ height: 4, backgroundColor: statusColor }} />

                    <CardContent sx={{ p: 2.5, flexGrow: 1 }}>
                      {/* Dossier + selected badge */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Chip
                          label={t('demandes.dossierNum', { id: item.id })}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32',
                          }}
                        />
                        {isSelected && (
                          <MdCheckCircle size={22} color="#D89D2F" />
                        )}
                      </Box>

                      {/* Flag + Title */}
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        {item.urlpays && (
                          <Box
                            sx={{
                              display: 'inline-block',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                              mb: 1,
                            }}
                          >
                            <img
                              src={item.urlpays}
                              alt="Flag"
                              onError={handleFlagError}
                              style={{ width: 48, height: 32, objectFit: 'cover', display: 'block' }}
                            />
                          </Box>
                        )}
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#333', lineHeight: 1.3 }}
                        >
                          {item.titre}
                        </Typography>
                      </Box>

                      {/* Departure date */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                          mb: 2,
                        }}
                      >
                        <MdFlightTakeoff size={18} color="#666" />
                        <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>
                          {item.datedepart}
                        </Typography>
                      </Box>

                      {/* Traveler */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1.5,
                          mb: 2,
                          py: 1.5,
                          backgroundColor: '#f8f9fa',
                          borderRadius: 2,
                        }}
                      >
                        {item.urlvoyageur && (
                          <Avatar
                            src={item.urlvoyageur}
                            alt={`${item.nom} ${item.prenom}`}
                            sx={{
                              width: 44,
                              height: 44,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              border: '2px solid white',
                            }}
                          />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>
                          {item.prenom} {item.nom}
                        </Typography>
                      </Box>

                      {/* ---- ÉTAT VISA ---- */}
                      {item.etat_visa === 'Formulaire complet' && !item.etat_paiement && (
                        <Chip
                          label={hideFees ? t('demandes.states.awaitingClientValidation') : (isClientSociete === false ? t('demandes.states.awaitingPayment') : t('demandes.states.awaitingBilling'))}
                          size="small"
                          icon={<InfoIcon message={
                            hideFees
                              ? t('demandes.tooltips.awaitingClientValidationHide')
                              : (isClientSociete === false
                                ? t('demandes.tooltips.awaitingPaymentSociete')
                                : t('demandes.tooltips.awaitingBilling'))
                          } />}
                          sx={{ mb: 1, fontWeight: 600, backgroundColor: '#e8f5e9', color: '#2e7d32', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {(item.etat_visa === 'en cours' || item.etat_visa === 'à traiter') &&
                        item.etat_paiement !== 'attente paiement' && (
                          <Chip
                            label={t('demandes.states.processing')}
                            size="small"
                            icon={<InfoIcon message={t('demandes.tooltips.processing')} />}
                            sx={{ mb: 1, fontWeight: 600, backgroundColor: '#e8f5e9', color: '#2e7d32', width: '100%', justifyContent: 'center' }}
                          />
                      )}

                      {item.etat_visa === 'Vérification éligibilité' && (
                        <Chip
                          label={t('demandes.states.eligibilityCheck')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.eligibilityCheck')} />}
                          sx={{ mb: 1, fontWeight: 600, backgroundColor: '#fff9c4', color: '#f57f17', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {(item.etat_visa === 'En attente réception dossier' || item.etat_visa === 'Attente réception dossier') && (
                        <Chip
                          label={t('demandes.states.awaitingFileReceipt')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.awaitingFileReceipt')} />}
                          sx={{ mb: 1, fontWeight: 600, backgroundColor: '#e8f5e9', color: '#2e7d32', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_visa === 'Dossier en souffrance' && (
                        <Chip
                          label={t('demandes.states.fileOverdue')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.fileOverdue')} />}
                          sx={{ mb: 1, fontWeight: 600, backgroundColor: '#ffebee', color: '#c62828', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_visa === 'Dossier reçu' && (
                        <Chip
                          label={t('demandes.states.fileReceived')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.fileReceived')} />}
                          sx={{ mb: 1, fontWeight: 600, backgroundColor: '#e8f5e9', color: '#2e7d32', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_visa === 'attente paiement' && hideFees && (
                        <Chip
                          label={t('demandes.states.paymentPending')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.paymentPending')} />}
                          sx={{ mb: 1, fontWeight: 600, backgroundColor: '#fff3e0', color: '#e65100', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {(item.etat_visa === 'à vérifier' || item.etat_visa === 'A vérifier') && (
                        <Chip
                          label={t('demandes.states.inProgress')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.processing')} />}
                          sx={{ mb: 1, fontWeight: 600, backgroundColor: '#e8f5e9', color: '#2e7d32', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_visa === 'A traiter' && hideFees && (
                        <Chip
                          label={t('demandes.states.inProgress')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.processing')} />}
                          sx={{ mb: 1, fontWeight: 600, backgroundColor: '#e8f5e9', color: '#2e7d32', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_visa === 'Reçu' && (
                        <Chip
                          label={t('demandes.states.fileReceivedChecking')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.fileReceivedChecking')} />}
                          sx={{ mb: 1, fontWeight: 600, backgroundColor: '#fff3e0', color: '#e65100', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_visa === 'Attente paiement' && (
                        <Chip
                          label={t('demandes.states.awaitingPayment')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.awaitingPaymentSociete')} />}
                          sx={{ mb: 1, fontWeight: 600, backgroundColor: '#fff3e0', color: '#e65100', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {/* ---- PRIX ---- */}
                      {!hideFees && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 1,
                            mt: 1.5,
                            mb: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          {item.assurancevisa === 'oui' && (
                            <Chip
                              label={t('demandes.insuranceAmount')}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500, borderColor: '#D89D2F', color: '#D89D2F', fontSize: '0.7rem' }}
                            />
                          )}
                          <Chip
                            label={t('demandes.totalAmount', { total: (item.prixconsulat ?? 0) + (item.prixintervention ?? 0) + (item.assurancevisa === 'oui' ? 20 : 0) })}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              backgroundColor: '#2e7d32',
                              color: 'white',
                              fontSize: '0.8rem',
                            }}
                          />
                        </Box>
                      )}

                      {/* Infosdemande */}
                      {item.infosdemande && (
                        <Box
                          sx={{
                            border: '2px dashed #bbb',
                            borderRadius: 2,
                            p: 1.5,
                            mt: 1.5,
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#555', textAlign: 'center', fontSize: '0.8rem' }}>
                            {item.infosdemande}
                          </Typography>
                        </Box>
                      )}

                      {/* ---- ETAT PAIEMENT ---- */}
                      {item.etat_paiement === 'attente paiement' && (
                        <Chip
                          label={t('demandes.states.awaitingPayment')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.paymentMail')} />}
                          sx={{ mt: 1, fontWeight: 600, backgroundColor: '#fff3e0', color: '#e65100', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_paiement === 'virement' && (
                        <Chip
                          label={t('demandes.states.transfer')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.transfer')} />}
                          sx={{ mt: 1, fontWeight: 600, backgroundColor: '#e8f5e9', color: '#2e7d32', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_paiement === 'payé' && (
                        <Chip
                          label={t('demandes.states.paid')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.paid')} />}
                          sx={{ mt: 1, fontWeight: 600, backgroundColor: '#e8f5e9', color: '#2e7d32', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_paiement === 'Attente facturation' && !hideFees && (
                        <Chip
                          label={isClientSociete === false ? t('demandes.states.awaitingPayment') : t('demandes.states.awaitingBilling')}
                          size="small"
                          icon={<InfoIcon message={isClientSociete === false
                            ? t('demandes.tooltips.awaitingPaymentSociete')
                            : t('demandes.tooltips.awaitingBilling')
                          } />}
                          sx={{ mt: 1, fontWeight: 600, backgroundColor: '#fff3e0', color: '#e65100', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_paiement === 'à facturer' && !hideFees && (
                        <Chip
                          label={t('demandes.states.paid')}
                          size="small"
                          icon={<InfoIcon message={t('demandes.tooltips.paid')} />}
                          sx={{ mt: 1, fontWeight: 600, backgroundColor: '#e8f5e9', color: '#2e7d32', width: '100%', justifyContent: 'center' }}
                        />
                      )}

                      {item.etat_paiement &&
                        !['attente paiement', 'virement', 'payé', 'facturé', 'à facturer', 'Attente facturation'].includes(
                          item.etat_paiement
                        ) && (
                          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: '#666' }}>
                            {item.etat_paiement}
                          </Typography>
                      )}

                      {/* Facturé => bouton facture */}
                      {item.etat_paiement === 'facturé' && (
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            mt: 1.5,
                            width: '100%',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            borderColor: '#D89D2F',
                            color: '#D89D2F',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            accederFacture(item.chemin);
                          }}
                        >
                          {t('demandes.buttons.accessInvoice')}
                        </Button>
                      )}
                    </CardContent>

                    {/* Corbeille */}
                    {isRemovable && (
                      <IconButton
                        sx={{
                          position: 'absolute',
                          right: 8,
                          bottom: 8,
                          color: '#e53935',
                          backgroundColor: '#ffebee',
                          '&:hover': { backgroundColor: '#ffcdd2' },
                          width: 32,
                          height: 32,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDemandeConfirmation(item.id!);
                        }}
                      >
                        <MdDelete size={18} />
                      </IconButton>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Bouton flottant paiement + résumé commande */}
        {!hideFees && (
          <Box
            sx={{
              position: 'fixed',
              bottom: { xs: 64, sm: 0 },
              left: { xs: 0, sm: 'auto' },
              right: 0,
              width: { xs: '100%', sm: 'calc(100% - 240px)' },
              backgroundColor: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(12px)',
              p: 2,
              borderTop: '1px solid rgba(0,0,0,0.08)',
              zIndex: 999,
              boxShadow: '0 -4px 20px rgba(0,0,0,0.06)',
            }}
          >
            {/* Order summary when items are selected */}
            {selectedIds.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label={t('demandes.selectionSummary', { count: selectedIds.length })}
                  sx={{ fontWeight: 600, backgroundColor: '#e3f2fd', color: '#B5811F' }}
                />
                {(() => {
                  const selectedDemandes = demandes.filter((d) => selectedIds.includes(d.id!));
                  const total = selectedDemandes.reduce((sum, d) => {
                    const base = (d.prixconsulat ?? 0) + (d.prixintervention ?? 0);
                    const assurance = d.assurancevisa === 'oui' ? 20 : 0;
                    return sum + base + assurance;
                  }, 0);
                  return (
                    <Chip
                      label={t('demandes.totalAmount', { total })}
                      sx={{ fontWeight: 700, backgroundColor: '#2e7d32', color: 'white', fontSize: '0.85rem' }}
                    />
                  );
                })()}
              </Box>
            )}

            <Box sx={{ textAlign: 'center' }}>
            {isClientSociete === true && (
              <Button
                variant="contained"
                onClick={handlePayer}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 3,
                  px: 4,
                  py: 1.2,
                  background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
                  boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B5811F 0%, #8C6418 100%)',
                  },
                }}
              >
                {t('demandes.buttons.billingInfo')}
              </Button>
            )}

            {isClientSociete === false && hasUnpaidDossiers && (
              <Button
                variant="contained"
                onClick={handlePayerSumup}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 3,
                  px: 4,
                  py: 1.2,
                  background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
                  boxShadow: '0 4px 14px rgba(25,118,210,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B5811F 0%, #8C6418 100%)',
                  },
                }}
              >
                {PAIEMENT_ACTIF ? t('demandes.buttons.payOrder') : t('demandes.buttons.launchOrder')}
              </Button>
            )}

            {isClientSociete === null && (
              <Button
                variant="contained"
                onClick={handlePayer}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 3,
                  px: 4,
                  py: 1.2,
                  background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
                  boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B5811F 0%, #8C6418 100%)',
                  },
                }}
              >
                {t('demandes.buttons.billingInfo')}
              </Button>
            )}
            </Box>
          </Box>
        )}

        {/* Boîte de dialogue d'info */}
        <Dialog
          open={infoDialogOpen}
          onClose={handleInfoClose}
          PaperProps={{
            sx: { borderRadius: 3, maxWidth: 440 },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>{t('demandes.infoDialogTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: '#555' }}>{infoDialogMessage}</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleInfoClose}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #B5811F 0%, #8C6418 100%)',
                },
              }}
              autoFocus
            >
              {tCommon('ok')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default DemandesScreen;
